import { useState, type FormEvent } from 'react';

const FORM_ADDR = 'https://readdy.ai/api/form/db7lm68mkl3spn8sma9g';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLen(0);
      return;
    }

    formData.delete('website_alt');
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      const failed =
        !response.ok ||
        parsed?.code !== 'OK' ||
        `${serverMsg} ${responseText}`.toLowerCase().includes('spam');

      if (failed) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLen(0);
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      id="about-contact-form"
      data-readdy-form
      action={FORM_ADDR}
      method="post"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950">在线留言</h2>
      <p className="mt-2 text-sm text-foreground-500">
        对内容有建议、希望补充资讯或合作洽谈，欢迎给我们留言。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-2 block text-sm font-medium text-foreground-800">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="请输入您的称呼"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-foreground-800">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="contact-message" className="mb-2 block text-sm font-medium text-foreground-800">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          onChange={(e) => setMessageLen(e.target.value.length)}
          placeholder="请填写您的留言（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        ></textarea>
        <p className="mt-1 text-right text-xs text-foreground-400">{messageLen} / 500</p>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="contact-verify-field"
      />

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交留言
            </>
          )}
        </button>

        {status === 'success' && (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-700">
            <i className="ri-checkbox-circle-fill"></i>
            留言已提交，感谢您的反馈！
          </span>
        )}
        {status === 'error' && (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-accent-700">
            <i className="ri-error-warning-fill"></i>
            {formError}
          </span>
        )}
      </div>
    </form>
  );
}